import { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Home, Plus, BarChart3, MoreHorizontal, BookOpen, Heart, Scale, LogOut, X, Target } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';

interface NavItem {
  to: string;
  icon: React.ReactNode;
  label: string;
  ariaLabel: string;
  exact?: boolean;
}

const mainNavItems: NavItem[] = [
  {
    to: '/',
    icon: <Home className="h-6 w-6" />,
    label: 'Koti',
    ariaLabel: 'Siirry etusivulle',
    exact: true,
  },
  {
    to: '/workouts/new',
    icon: <Plus className="h-6 w-6" />,
    label: 'Uusi',
    ariaLabel: 'Luo uusi treeni',
  },
  {
    to: '/stats',
    icon: <BarChart3 className="h-6 w-6" />,
    label: 'Tilastot',
    ariaLabel: 'Näytä tilastot',
  },
];

const moreMenuItems: NavItem[] = [
  {
    to: '/programs',
    icon: <BookOpen className="h-5 w-5" />,
    label: 'Ohjelmat',
    ariaLabel: 'Näytä treeniohjelmat',
  },
  {
    to: '/kyykkyohjelma',
    icon: <Target className="h-5 w-5" />,
    label: 'Kyykkyohjelma',
    ariaLabel: 'Näytä kyykkyohjelma',
  },
  {
    to: '/health',
    icon: <Heart className="h-5 w-5" />,
    label: 'Terveys',
    ariaLabel: 'Näytä terveystiedot',
  },
  {
    to: '/weight',
    icon: <Scale className="h-5 w-5" />,
    label: 'Paino',
    ariaLabel: 'Näytä painonseuranta',
  },
];

export function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Close menu on escape key
  useEffect(() => {
    if (!isMenuOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMenuOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMenuOpen]);

  const isActive = (item: NavItem) => {
    if (item.exact) {
      return location.pathname === item.to;
    }
    return location.pathname.startsWith(item.to);
  };

  const isMoreMenuActive = moreMenuItems.some(item => isActive(item));

  const handleLogout = async () => {
    setIsMenuOpen(false);
    await logout();
  };

  const handleMenuItemClick = (to: string) => {
    setIsMenuOpen(false);
    navigate(to);
  };

  return (
    <>
      {/* Backdrop */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setIsMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Bottom Sheet Menu */}
      <div
        className={cn(
          'fixed left-0 right-0 z-50 md:hidden bg-white dark:bg-gray-800 rounded-t-2xl shadow-2xl transition-transform duration-300 ease-out',
          isMenuOpen ? 'translate-y-0' : 'translate-y-full'
        )}
        style={{ bottom: '64px' }}
        role="dialog"
        aria-modal="true"
        aria-label="Lisävalikko"
      >
        <div className="p-4">
          {/* Handle bar */}
          <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto mb-4" />

          {/* Menu items */}
          <div className="space-y-1">
            {moreMenuItems.map((item) => {
              const active = isActive(item);
              return (
                <button
                  key={item.to}
                  onClick={() => handleMenuItemClick(item.to)}
                  aria-label={item.ariaLabel}
                  className={cn(
                    'w-full flex items-center gap-4 px-4 py-3 rounded-xl transition-colors touch-manipulation',
                    'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500',
                    active
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 active:bg-gray-100 dark:active:bg-gray-700'
                  )}
                >
                  {item.icon}
                  <span className="font-medium">{item.label}</span>
                  {active && (
                    <span className="ml-auto w-2 h-2 bg-primary-600 dark:bg-primary-400 rounded-full" />
                  )}
                </button>
              );
            })}

            {/* Divider */}
            <hr className="my-2 border-gray-200 dark:border-gray-700" />

            {/* Logout */}
            <button
              onClick={handleLogout}
              aria-label="Kirjaudu ulos"
              className="w-full flex items-center gap-4 px-4 py-3 rounded-xl text-red-600 dark:text-red-400 active:bg-red-50 dark:active:bg-red-900/20 transition-colors touch-manipulation focus:outline-hidden focus-visible:ring-2 focus-visible:ring-red-500"
            >
              <LogOut className="h-5 w-5" />
              <span className="font-medium">Kirjaudu ulos</span>
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Navigation Bar */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg"
        role="navigation"
        aria-label="Päänavigaatio"
      >
        <div className="pb-safe-bottom">
          <div className="flex items-center justify-around h-16">
            {/* Main nav items */}
            {mainNavItems.map((item) => {
              const active = isActive(item);
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  aria-label={item.ariaLabel}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'flex flex-col items-center justify-center min-w-[64px] min-h-[44px] px-3 py-2',
                    'rounded-lg transition-all duration-200 touch-manipulation',
                    'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                    active
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-gray-500 dark:text-gray-400 active:text-gray-700 dark:active:text-gray-300 active:bg-gray-100 dark:active:bg-gray-700'
                  )}
                >
                  <div
                    className={cn(
                      'relative flex items-center justify-center w-12 h-8 rounded-full transition-all duration-200',
                      active && 'bg-primary-100 dark:bg-primary-900/30'
                    )}
                  >
                    {item.icon}
                    {active && (
                      <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary-600 dark:bg-primary-400 rounded-full" />
                    )}
                  </div>
                  <span
                    className={cn(
                      'text-xs font-medium mt-1 transition-colors duration-200',
                      active && 'text-primary-600 dark:text-primary-400'
                    )}
                  >
                    {item.label}
                  </span>
                </NavLink>
              );
            })}

            {/* More button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? 'Sulje lisävalikko' : 'Avaa lisävalikko'}
              aria-expanded={isMenuOpen}
              aria-controls="more-menu"
              className={cn(
                'flex flex-col items-center justify-center min-w-[64px] min-h-[44px] px-3 py-2',
                'rounded-lg transition-all duration-200 touch-manipulation',
                'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                isMenuOpen || isMoreMenuActive
                  ? 'text-primary-600 dark:text-primary-400'
                  : 'text-gray-500 dark:text-gray-400 active:text-gray-700 dark:active:text-gray-300 active:bg-gray-100 dark:active:bg-gray-700'
              )}
            >
              <div
                className={cn(
                  'relative flex items-center justify-center w-12 h-8 rounded-full transition-all duration-200',
                  (isMenuOpen || isMoreMenuActive) && 'bg-primary-100 dark:bg-primary-900/30'
                )}
              >
                {isMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <MoreHorizontal className="h-6 w-6" />
                )}
                {isMoreMenuActive && !isMenuOpen && (
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary-600 dark:bg-primary-400 rounded-full" />
                )}
              </div>
              <span
                className={cn(
                  'text-xs font-medium mt-1 transition-colors duration-200',
                  (isMenuOpen || isMoreMenuActive) && 'text-primary-600 dark:text-primary-400'
                )}
              >
                Lisää
              </span>
            </button>
          </div>
        </div>
      </nav>
    </>
  );
}
